import { useEffect } from "react";

// The customer app's type, colour and shape in one place. theme.js reads the
// fonts from here so every MUI screen inherits them; hand-styled screens import
// the same tokens instead of writing their own font stacks.
//
// Two fonts only: Inter for everything you read or tap, Bricolage Grotesque
// for headings and display text. Both load once in public/index.html.

export const FONT_BODY = 'Inter, system-ui, -apple-system, "Segoe UI", sans-serif';
export const FONT_DISPLAY = '"Bricolage Grotesque", Inter, system-ui, -apple-system, "Segoe UI", sans-serif';

// Type scale (px). Large headings carry a slight negative tracking.
export const TYPE = {
  pageTitle: { fontFamily: FONT_DISPLAY, fontSize: 34, fontWeight: 800, lineHeight: 1.1, letterSpacing: "-0.02em" },
  sectionTitle: { fontFamily: FONT_DISPLAY, fontSize: 22, fontWeight: 700, lineHeight: 1.2, letterSpacing: "-0.015em" },
  cardTitle: { fontFamily: FONT_DISPLAY, fontSize: 18, fontWeight: 700, lineHeight: 1.25, letterSpacing: "-0.01em" },
  itemName: { fontFamily: FONT_BODY, fontSize: 15, fontWeight: 600, lineHeight: 1.35 },
  subHeading: { fontFamily: FONT_BODY, fontSize: 15, fontWeight: 700, lineHeight: 1.35 },
  body: { fontFamily: FONT_BODY, fontSize: 14, fontWeight: 400, lineHeight: 1.5 },
  bodyStrong: { fontFamily: FONT_BODY, fontSize: 15, fontWeight: 500, lineHeight: 1.5 },
  meta: { fontFamily: FONT_BODY, fontSize: 13, fontWeight: 500, lineHeight: 1.4 },
  label: { fontFamily: FONT_BODY, fontSize: 12, fontWeight: 600, lineHeight: 1.3 },
};

// Every grey used for text is C.muted or darker, so small text keeps 4.5:1.
export const C = {
  bg: "#FFFFFF",
  ink: "#1A1918",
  muted: "#5C5953",
  red: "#C42B1F",
  redHover: "#AD2519",
  redDark: "#962117",
  redLink: "#B3241A",
  redSoft: "#FBECEA",
  // A selected option card or tip.
  redTint: "#FDF0EE",
  border: "#ECEAE5",
  borderStrong: "#DDDAD3",
  surface: "#F7F6F3",
  surface2: "#F2F0EC",
  green: "#17633A",
  greenSoft: "#E8F5EC",
  greenTint: "#F3FAF5",
  gold: "#8A5A00",
  goldAccent: "#F5B731",
  // Wallet and rewards surfaces.
  goldSoft: "#FDF4E1",
  teal: "#0F5C73",
  tealSoft: "#E3F1F5",
  disabledBg: "#E7E5E0",
  disabledText: "#5C5953",
};

export const RADIUS = {
  hero: "24px",
  card: "20px",
  menuCard: "16px",
  control: "12px",
  controlLg: "14px",
  pill: "999px",
};

// Soft shadows, for floating elements only (info card, cart, add buttons).
export const SHADOW = {
  float: "0 12px 32px rgba(26,25,24,0.08), 0 2px 6px rgba(26,25,24,0.05)",
  button: "0 2px 8px rgba(26,25,24,0.16)",
};

// A visible keyboard focus ring for hand-built controls.
export const FOCUS_RING = {
  "&:focus-visible": { outline: `2px solid ${C.ink}`, outlineOffset: "2px" },
};

// Text for screen readers only. Sizes are strings on purpose: in sx a bare 1
// means 100%, which stretched these labels across the page.
export const VISUALLY_HIDDEN = {
  position: "absolute",
  width: "1px",
  height: "1px",
  p: 0,
  m: "-1px",
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
};

// A <button> with the browser's chrome stripped, ready to be styled.
export const BUTTON_RESET = {
  appearance: "none",
  border: 0,
  margin: 0,
  padding: 0,
  background: "none",
  font: "inherit",
  color: "inherit",
  cursor: "pointer",
  textAlign: "inherit",
};

// One half of the top bar's location · time pill.
export const pillTriggerSx = {
  ...BUTTON_RESET,
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  height: 36,
  px: "10px",
  borderRadius: RADIUS.pill,
  fontFamily: FONT_BODY,
  fontSize: 14,
  fontWeight: 600,
  color: C.ink,
  whiteSpace: "nowrap",
  transition: "background-color .15s ease",
  "&:hover": { backgroundColor: C.surface2 },
  ...FOCUS_RING,
};

// The fixed top bar publishes its height as --rmf-topbar-h so the pages below
// can offset their content and stick their own bars right under it, whatever
// it wraps to.
export const TOPBAR_VAR = "--rmf-topbar-h";
export const topBarOffset = (extra = 0) => `calc(var(${TOPBAR_VAR}, 64px) + ${extra}px)`;

export function readTopBarHeight() {
  const v = parseFloat(getComputedStyle(document.documentElement).getPropertyValue(TOPBAR_VAR));
  return Number.isFinite(v) ? v : 64;
}

export function useTopBarHeight(ref) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const publish = () =>
      document.documentElement.style.setProperty(TOPBAR_VAR, `${Math.round(el.getBoundingClientRect().height)}px`);
    publish();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
}
